import { forwardRef } from 'react'
import { Link, LinkProps } from 'react-router'

import { cn } from '@shared/lib/cn'

import styles from './styles.module.scss'

export type ButtonSizeVariant = 'small' | 'medium' | 'large'
export type ButtonShapeVariant = 'square' | 'default'
export type ButtonThemeVariant = 'primary' | 'secondary' | 'grey' | 'white' | 'quaternary' | 'none'

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement>;
export type AnchorProps = LinkProps &
  React.AnchorHTMLAttributes<HTMLAnchorElement>;

export interface ButtonComponentProps
  extends Partial<ButtonProps & AnchorProps> {
  to?: string;
  size?: ButtonSizeVariant;
  theme?: ButtonThemeVariant;
  shape?: ButtonShapeVariant;
  /** Skip Link component and use HTML anchor - this can be useful when you dont have router context */
  asHTMLAnchor?: boolean;
}

const Button = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonComponentProps
>(
  (
    { to, children, className, theme = 'primary', size = 'medium', shape = 'default-shape', asHTMLAnchor = false, ...props },
    ref,
  ) => {
    const baseClassName = cn(
      styles.root,
      className,
      styles[theme],
      styles[size],
      styles[shape]
    )

    if (to) {

      if (asHTMLAnchor) {
        return (
          <a href={to} ref={ref as React.Ref<HTMLAnchorElement>} className={baseClassName} {...props}>
            {children}
          </a>
        )
      }

      return (
        <Link
          to={to}
          ref={ref as React.Ref<HTMLAnchorElement>}
          className={baseClassName}
          {...props}
        >
          {children}
        </Link>
      )
    }

    return (
      <button
        type="button"
        ref={ref as React.Ref<HTMLButtonElement>}
        className={cn(
          'reset-button',
          baseClassName
        )}
        {...props}
      >
        {children}
      </button>
    )
  },
)

Button.displayName = 'Button'

export default Button
